import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, ChevronRight, Loader2, X } from "lucide-react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/utils/cn";
import { usePermissions } from "@/hooks/usePermissions";
import type { ProgramDetailDto } from "@/types/grc";
import {
  softHoldKeys,
  useResolveSoftHold,
  useSoftHoldById,
  useSoftHoldHistoryById,
  useSoftHoldsByMonth,
  useUpdatePreferredDateSelectionStatus
} from "@/services/softHolds";
import type { SoftHoldPreferredDate, SoftHoldResponse } from "@/types/softHolds";

interface SoftHoldsFlyoutProps {
  month: number;
  year: number;
  initialSoftHoldId: number;
  draftItems?: ProgramDetailDto[];
  includeDraftItems?: boolean;
  listOnly?: boolean;
  onSoftHoldItemClick?: (softHoldId: number) => void;
  onDraftItemClick?: (draftMeetingId: number) => void;
  onClose: () => void;
}

type TabKey = "details" | "history";

const RESOLVE_CONFIRMED_EVENT = "Confirmed Event";
const RESOLVE_DID_NOT_MATERIALIZE = "Did Not Materialize";

export function SoftHoldsFlyout({
  month,
  year,
  initialSoftHoldId,
  draftItems = [],
  includeDraftItems = true,
  listOnly = false,
  onSoftHoldItemClick,
  onDraftItemClick,
  onClose
}: SoftHoldsFlyoutProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEditSoftHold = hasPermission("CanEditSoftHold");

  const [selectedSoftHoldId, setSelectedSoftHoldId] = useState(initialSoftHoldId);
  const [activeTab, setActiveTab] = useState<TabKey>("details");
  const [showResolveDialog, setShowResolveDialog] = useState(false);

  const [resolveStatus, setResolveStatus] = useState("");
  const [resolvePreferredDateId, setResolvePreferredDateId] = useState<number | "">("");
  const [resolveNotes, setResolveNotes] = useState("");

  const [resolveStatusError, setResolveStatusError] = useState<string | null>(null);
  const [resolveDateError, setResolveDateError] = useState<string | null>(null);
  const hasValidMonthYear = month > 0 && year > 0;
  const hasSelectedSoftHold = selectedSoftHoldId > 0;

  const { data: softHolds, isPending: isListPending } = useSoftHoldsByMonth(month, year);

  const { data: selectedSoftHold, isPending: isDetailsPending } = useSoftHoldById(selectedSoftHoldId, true);

  const { data: history, isPending: isHistoryPending } = useSoftHoldHistoryById(
    selectedSoftHoldId,
    activeTab === "history"
  );

  const updatePreferredDate = useUpdatePreferredDateSelectionStatus();
  const resolveSoftHold = useResolveSoftHold();
  const visibleDraftItems = includeDraftItems ? draftItems : [];

  const resolveReasons = useMemo(() => {
    if (!selectedSoftHold?.meetingId) return [RESOLVE_DID_NOT_MATERIALIZE];
    return [RESOLVE_CONFIRMED_EVENT, RESOLVE_DID_NOT_MATERIALIZE];
  }, [selectedSoftHold?.meetingId]);

  useEffect(() => {
    const firstId = softHolds?.[0]?.id ?? 0;
    if (selectedSoftHoldId <= 0 && firstId > 0) setSelectedSoftHoldId(firstId);
  }, [softHolds, selectedSoftHoldId]);

  useEffect(() => {
    setSelectedSoftHoldId(initialSoftHoldId);
  }, [initialSoftHoldId]);

  useEffect(() => {
    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
  }, []);

  function handlePanelKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key !== "Tab") return;

    const focusables = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  async function handlePreferredDateToggle(
    softHoldId: number,
    preferredDateId: number,
    isSelected: boolean
  ) {
    if (!canEditSoftHold) return;

    try {
      await updatePreferredDate.mutateAsync({
        softHoldId,
        preferredDateId,
        isSelected
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: softHoldKeys.byMonth(month, year) }),
        queryClient.invalidateQueries({ queryKey: softHoldKeys.byId(softHoldId) })
      ]);
    } catch {
      toast.error("Unable to update preferred date selection.");
    }
  }

  function openResolveDialog() {
    setResolveStatus("");
    setResolvePreferredDateId("");
    setResolveNotes("");
    setResolveStatusError(null);
    setResolveDateError(null);
    setShowResolveDialog(true);
  }

  async function handleResolveSave() {
    if (!selectedSoftHold) return;

    let hasError = false;

    if (!resolveStatus) {
      setResolveStatusError("Please select reason to resolve soft hold.");
      hasError = true;
    } else {
      setResolveStatusError(null);
    }

    if (resolveStatus === RESOLVE_CONFIRMED_EVENT && !resolvePreferredDateId) {
      setResolveDateError("Please select confirmed date.");
      hasError = true;
    } else {
      setResolveDateError(null);
    }

    if (hasError) return;

    try {
      await resolveSoftHold.mutateAsync({
        softHoldId: selectedSoftHold.id,
        status: resolveStatus,
        meetingId: selectedSoftHold.meetingId || undefined,
        resolvedPreferredDateId:
          resolveStatus === RESOLVE_CONFIRMED_EVENT && resolvePreferredDateId
            ? Number(resolvePreferredDateId)
            : undefined,
        notes: resolveNotes.trim() || undefined
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: softHoldKeys.byMonth(month, year) }),
        queryClient.invalidateQueries({ queryKey: softHoldKeys.byId(selectedSoftHold.id) }),
        queryClient.invalidateQueries({ queryKey: softHoldKeys.historyById(selectedSoftHold.id) })
      ]);

      toast.success("Soft hold resolved successfully.");
      setShowResolveDialog(false);
    } catch {
      toast.error("Unable to resolve soft hold.");
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/30" onClick={onClose} aria-hidden="true" />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="soft-holds-flyout-title"
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 ml-auto flex h-full w-full max-w-[900px] flex-col border-l border-slate-300 bg-white shadow-xl"
        onKeyDown={handlePanelKeyDown}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 id="soft-holds-flyout-title" className="text-base font-semibold text-slate-900">
            Soft Holds
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close soft holds dialog"
            className="text-slate-500 hover:text-slate-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div
          className={cn(
            "grid flex-1 grid-cols-1 overflow-hidden",
            !listOnly && "md:grid-cols-[360px_1fr]"
          )}
        >
          <div className="overflow-auto border-b border-slate-200 md:border-r md:border-b-0">
            {hasValidMonthYear && isListPending ? (
              <LoadingState message="Loading soft holds..." />
            ) : (!softHolds || softHolds.length === 0) && visibleDraftItems.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-500">No soft holds found for this month.</p>
            ) : (
              <ul className="divide-y divide-slate-200">
                {(softHolds ?? []).map((softHold, idx) => {
                  const isSelected = softHold.id === selectedSoftHoldId;
                  return (
                    <li
                      key={softHold.id}
                      className={cn(
                        "px-3 py-3",
                        idx % 2 === 0 ? "bg-white" : "bg-slate-50",
                        isSelected && "bg-amber-50"
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            if (listOnly) {
                              onSoftHoldItemClick?.(softHold.id);
                              return;
                            }
                            setSelectedSoftHoldId(softHold.id);
                          }}
                          className="min-w-0 flex-1 text-left"
                          aria-label={`Open ${softHold.meetingName}`}
                        >
                          <p className="truncate text-sm font-semibold text-slate-900">
                            {softHold.meetingIdDisplay || "------"} | {softHold.meetingName}
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (listOnly) {
                              onSoftHoldItemClick?.(softHold.id);
                              return;
                            }
                            setSelectedSoftHoldId(softHold.id);
                          }}
                          aria-label={`View details for ${softHold.meetingName}`}
                          className="rounded border border-slate-300 p-1 text-slate-600 hover:bg-slate-100"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="mt-2 space-y-1">
                        {softHold.preferredDates.map((date) => (
                          <div key={date.id} className="flex items-center gap-2 text-xs text-slate-700">
                            <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                            {!listOnly && (
                              <input
                                type="checkbox"
                                checked={date.isSelected}
                                onChange={(e) =>
                                  void handlePreferredDateToggle(softHold.id, date.id, e.target.checked)
                                }
                                disabled={!canEditSoftHold || updatePreferredDate.isPending}
                                className="h-4 w-4 rounded border-slate-300"
                                aria-label={`Select preferred date ${formatDateRange(date)}`}
                              />
                            )}
                            <span>{formatDateRange(date)}</span>
                          </div>
                        ))}
                      </div>
                    </li>
                  );
                })}

                {visibleDraftItems.map((draftItem, idx) => {
                  const rowIndex = (softHolds?.length ?? 0) + idx;
                  const dateRanges = getDraftDateRanges(draftItem);

                  return (
                    <li
                      key={`draft-${draftItem.softHoldId}`}
                      className={cn("px-3 py-3", rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50")}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => onDraftItemClick?.(draftItem.softHoldId)}
                          className="min-w-0 flex-1 text-left"
                          aria-label={`Open draft meeting ${draftItem.eventName}`}
                        >
                          <p className="truncate text-sm font-semibold text-slate-900">
                            {draftItem.eventId || "------"} | {draftItem.eventName}
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => onDraftItemClick?.(draftItem.softHoldId)}
                          aria-label={`View draft details for ${draftItem.eventName}`}
                          className="rounded border border-slate-300 p-1 text-slate-600 hover:bg-slate-100"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="mt-2 space-y-1">
                        {dateRanges.map((range, dateIndex) => (
                          <div
                            key={`${draftItem.softHoldId}-${dateIndex}`}
                            className="flex items-center gap-2 text-xs text-slate-700"
                          >
                            <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                            <span>
                              {toDateDisplay(range.startDate)} - {toDateDisplay(range.endDate)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {!listOnly && (
            <div className="flex min-h-0 flex-col overflow-hidden">
            <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-2">
              <button
                type="button"
                onClick={() => setActiveTab("details")}
                className={cn(
                  "rounded px-3 py-1 text-sm",
                  activeTab === "details"
                    ? "bg-slate-900 text-white"
                    : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                )}
              >
                Details
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("history")}
                className={cn(
                  "rounded px-3 py-1 text-sm",
                  activeTab === "history"
                    ? "bg-slate-900 text-white"
                    : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                )}
              >
                History
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
              {activeTab === "details" ? (
                hasSelectedSoftHold && isDetailsPending ? (
                  <LoadingState message="Loading soft hold details..." />
                ) : !selectedSoftHold ? (
                  <p className="text-sm text-slate-500">Select a soft hold to view details.</p>
                ) : (
                  <SoftHoldDetailsPanel softHold={selectedSoftHold} />
                )
              ) : hasSelectedSoftHold && isHistoryPending ? (
                <LoadingState message="Loading soft hold history..." />
              ) : !history || history.length === 0 ? (
                <p className="text-sm text-slate-500">No soft hold history.</p>
              ) : (
                <ul className="space-y-2">
                  {history.map((item, idx) => (
                    <li
                      key={`${item.softHoldId}-${idx}`}
                      className={cn(
                        "flex items-start justify-between gap-4 rounded border border-slate-200 px-3 py-2",
                        idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                      )}
                    >
                      <span className="text-sm text-slate-800">{item.displayMessage}</span>
                      <span className="whitespace-nowrap text-xs text-slate-500">{item.createdDate}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
          <span className="text-sm text-slate-700">
            Total Add: <strong>{(softHolds?.length ?? 0) + visibleDraftItems.length}</strong>
          </span>

          <div className="flex items-center gap-2">
            {!listOnly && selectedSoftHold && !selectedSoftHold.isResolved && canEditSoftHold && (
              <button
                type="button"
                onClick={openResolveDialog}
                className="rounded border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800"
              >
                Resolve
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {showResolveDialog && selectedSoftHold && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="resolve-soft-hold-title"
          onClick={() => setShowResolveDialog(false)}
        >
          <div
            className="w-full max-w-xl rounded border border-slate-200 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-200 px-4 py-3">
              <h3 id="resolve-soft-hold-title" className="text-base font-semibold text-slate-900">
                Resolve Soft Hold
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Please select the reason for resolving this item.
              </p>
            </div>

            <div className="space-y-4 px-4 py-4">
              <div>
                <label htmlFor="resolve-reason" className="mb-1 block text-sm font-medium text-slate-800">
                  Select Reason <span className="text-red-600">*</span>
                </label>
                <select
                  id="resolve-reason"
                  value={resolveStatus}
                  onChange={(e) => {
                    setResolveStatus(e.target.value);
                    setResolveStatusError(null);
                    setResolveDateError(null);
                    setResolvePreferredDateId("");
                  }}
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">--</option>
                  {resolveReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
                {resolveStatusError && <p className="mt-1 text-xs text-red-600">{resolveStatusError}</p>}
              </div>

              {resolveStatus === RESOLVE_CONFIRMED_EVENT && (
                <>
                  <div>
                    <label htmlFor="resolve-confirmed-date" className="mb-1 block text-sm font-medium text-slate-800">
                      Select Confirmed Date(s) <span className="text-red-600">*</span>
                    </label>
                    <select
                      id="resolve-confirmed-date"
                      value={resolvePreferredDateId}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : "";
                        setResolvePreferredDateId(value);
                        setResolveDateError(null);
                      }}
                      className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="">--</option>
                      {selectedSoftHold.preferredDates.map((date) => (
                        <option key={date.id} value={date.id}>
                          {formatDateRange(date)}
                        </option>
                      ))}
                    </select>
                    {resolveDateError && <p className="mt-1 text-xs text-red-600">{resolveDateError}</p>}
                  </div>

                  <div>
                    <label htmlFor="resolve-meeting-id" className="mb-1 block text-sm font-medium text-slate-800">Meeting ID</label>
                    <input
                      id="resolve-meeting-id"
                      type="text"
                      value={selectedSoftHold.meetingId || "---"}
                      readOnly
                      className="w-full rounded border border-slate-300 bg-slate-100 px-3 py-2 text-sm text-slate-600"
                    />
                  </div>
                </>
              )}

              <div>
                <label htmlFor="resolve-notes" className="mb-1 block text-sm font-medium text-slate-800">Notes</label>
                <textarea
                  id="resolve-notes"
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  rows={4}
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
              <button
                type="button"
                onClick={() => setShowResolveDialog(false)}
                disabled={resolveSoftHold.isPending}
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleResolveSave()}
                disabled={resolveSoftHold.isPending}
                className="inline-flex items-center gap-2 rounded border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {resolveSoftHold.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SoftHoldDetailsPanel({ softHold }: { softHold: SoftHoldResponse }) {
  return (
    <div className="space-y-5">
      <section>
        <h4 className="mb-2 text-sm font-semibold text-slate-900">General Information</h4>
        <dl className="grid grid-cols-1 gap-2 text-sm text-slate-700 md:grid-cols-2">
          <Detail label="Meeting ID" value={softHold.meetingIdDisplay || "------"} />
          <Detail label="Meeting Name" value={softHold.meetingName} />
          <Detail label="Meeting Type" value={softHold.meetingType} />
          <Detail label="Business Advisor" value={softHold.businessAdvisor || "-"} />
          <Detail label="Organizer" value={softHold.organizerName || "-"} />
          <Detail label="Email" value={softHold.email || "-"} />
          <Detail label="Category" value={softHold.meetingCategory || "-"} />
          <Detail label="Subcategory" value={softHold.meetingSubCategory || "-"} />
          <Detail label="Created" value={toDateDisplay(softHold.createdDate)} />
          <Detail label="Status" value={softHold.isResolved ? "Resolved" : "Open"} />
        </dl>
      </section>

      <section>
        <h4 className="mb-2 text-sm font-semibold text-slate-900">Preferred Dates</h4>
        <ul className="space-y-2">
          {softHold.preferredDates.map((date) => (
            <li
              key={date.id}
              className="rounded border border-slate-200 px-3 py-2 text-sm text-slate-700"
            >
              <div className="flex items-center justify-between gap-2">
                <span>{formatDateRange(date)}</span>
                {date.isItPrimaryDate && (
                  <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                    Primary
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Meeting: {toDateDisplay(date.meetingStartDate)} - {toDateDisplay(date.meetingEndDate)}
              </p>
            </li>
          ))}
        </ul>
      </section>

      {softHold.notes && (
        <section>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Notes</h4>
          <p className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            {softHold.notes}
          </p>
        </section>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-800">{value || "-"}</dd>
    </div>
  );
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center py-10 text-sm text-slate-500">
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      {message}
    </div>
  );
}

function formatDateRange(date: SoftHoldPreferredDate) {
  return `${toDateDisplay(date.startDate)} - ${toDateDisplay(date.endDate)}`;
}

function getDraftDateRanges(program: ProgramDetailDto) {
  const ranges = Object.values(program.roomBlockDtos)
    .filter((roomBlock) => roomBlock.prefereBlockId !== 0 && roomBlock.blocked > 0)
    .reduce<Record<number, Date[]>>((acc, roomBlock) => {
      const groupKey = roomBlock.prefereBlockId;
      const blockDate = new Date(roomBlock.startDate);
      if (Number.isNaN(blockDate.getTime())) return acc;
      if (!acc[groupKey]) acc[groupKey] = [];
      acc[groupKey].push(blockDate);
      return acc;
    }, {});

  return Object.values(ranges)
    .filter((dates) => dates.length > 0)
    .map((dates) => {
      const sorted = dates.sort((a, b) => a.getTime() - b.getTime());
      return {
        startDate: sorted[0].toISOString(),
        endDate: sorted[sorted.length - 1].toISOString()
      };
    });
}

function toDateDisplay(value: string | null | undefined) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return format(date, "MM-dd-yyyy");
}
